import { useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  ButtonBase,
  Chip,
  SwipeableDrawer,
  Divider,
  Button
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import VisibilityRounded from "@mui/icons-material/VisibilityRounded";
import EditRounded from "@mui/icons-material/EditRounded";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import CancelRounded from "@mui/icons-material/CancelRounded";

import type { Bot, BrokerAccount } from "@/data/mock-data";
import type { Order, OrderStatus } from "@/data/mock-orders";
import { useRealHistoricalPrices } from "@/lib/whb-data";
import { calcOrderFifoResult } from "@/lib/realized-profit-fifo";
import { groupOrdersByMinute } from "@/data/mock-orders";
import { formatUSD } from "@/lib/formatters";
import type { OrdersFilterState } from "@/components/OrdersFilterBar";
import { MANUAL_ORDER_BOT_ID } from "@/components/OrdersFilterBar";
import { TickerAvatar } from "@/components/TickerAvatar";
import { useSwipeReveal } from "@/hooks/useSwipeReveal";
import { ItemActionSheet } from "@/components/ItemActionSheet";
import type { ItemAction } from "@/components/ItemActionSheet";
import { desktopBottomDrawerPaperSx } from "@/theme/drawer-sx";

// ---- Public component -----------------------------------------------------

interface OrdersSimpleViewProps {
  /** Already filtered — parent applies `applyOrdersFilter` upstream. */
  orders: Order[];
  /** Bots for botId → name lookup inside expand panels. */
  bots: Bot[];
  /**
   * All broker accounts — used to resolve `order.accountId` → account name
   * in the expand panel when we're showing orders from more than one account
   * (aggregate "All Live Accounts" view). Optional: if omitted or
   * `showAccount` is false, the Account row is hidden.
   */
  accounts?: BrokerAccount[];
  /**
   * Whether to surface the owning account in the expand panel. Only makes
   * sense when `orders` spans more than one account — otherwise every row
   * would show the same account name and the row is just noise.
   */
  showAccount?: boolean;
  /**
   * Optional callback invoked when a value inside the expand panel is
   * tapped — surfaces a "filter patch" (partial filter state) that the
   * parent should merge into its `OrdersFilterState`. Lets users drill
   * by tapping e.g. a ticker or bot name directly in an order's detail
   * card. Omit to disable click-to-filter entirely.
   */
  onApplyFilter?: (patch: Partial<OrdersFilterState>) => void;
  /**
   * Unfiltered orders for the same accounts in scope — used by the
   * detail drawer to compute realized gain/loss via FIFO matching.
   * The visible `orders` prop above may have been narrowed by the
   * user's filter bar (e.g. "last 30 days"), which would hide older
   * buys whose cost basis we still need. When omitted, the detail
   * drawer falls back to `orders` (correct for demo / unfiltered
   * mode; missing data only matters when filters are actually on).
   */
  allAccountOrders?: Order[];
  /**
   * Live ticker → company-name overrides from the same
   * /api/market/quotes pipeline as the chart. Wins over the static
   * table when present and meaningfully different from the symbol.
   */
  tickerNames?: Record<string, string>;
}

/**
 * Mobile orders list.
 *
 * Rendering model:
 *  • `groupOrdersByMinute` buckets orders that share a `submittedAt`
 *    clock minute — this visually groups a bot's rotation (8 rows
 *    under one header like "Apr 17, 2026 at 3:52 PM").
 *  • A group with a single item drops the time and shows just the
 *    date — a one-off manual order doesn't need a "3:41 PM" header
 *    that looks pretentious for a single trade.
 *  • Rows are divider-separated, not cards. Aims for a native-feeling
 *    list, not a stack of boxes.
 *  • Tapping a row expands inline via `Collapse`. The expanded panel
 *    carries the legacy WHB detail fields (Status, Quantity, Avg Cost,
 *    Total, Bot, Account, State) — we picked inline over a bottom
 *    sheet because it saves one layer of modal navigation.
 *
 * The list is pure presentation — empty-state handling, filtering,
 * and search all live in the parent's filter layer.
 */
export function OrdersSimpleView({
  orders,
  bots,
  accounts,
  showAccount,
  onApplyFilter,
  allAccountOrders,
  tickerNames
}: OrdersSimpleViewProps) {
  // One sheet is rendered at the list level (rather than per-row) so
  // only a single Drawer is ever mounted. `menuOrder` holds the row the
  // user just swiped; closing the sheet clears it.
  const [menuOrder, setMenuOrder] = useState<Order | null>(null);

  // 2026-04-25 Sergey: replaces the per-row inline Collapse panel.
  // Tapping a row now opens a bottom-sheet detail drawer (matching the
  // Buying Power → BalancesView pattern) so order details get a proper
  // dedicated context. Single drawer at the list level — only one is
  // ever mounted regardless of list length.
  const [detailOrder, setDetailOrder] = useState<Order | null>(null);

  if (orders.length === 0) {
    return (
      <Box
        sx={{
          py: 6,
          textAlign: "center",
          color: "text.secondary"
        }}
      >
        <Typography variant="body2">No orders match your filters.</Typography>
      </Box>
    );
  }

  // Orders come in whatever order the caller sorted them — newest
  // first is the assumption baked into mock-orders.ts. Bucket by
  // minute so the chrono-descending list still pools batches under
  // one header.
  const groups = groupOrdersByMinute(orders);

  // Actions for orders. All no-op placeholders for now — the sheet just
  // closes. When real wiring lands, each `onSelect` picks up the item
  // from the captured closure.
  const buildOrderActions = (o: Order): ItemAction[] => {
    const canCancel = o.status === "open" || o.status === "partial";
    return [
      {
        id: "view",
        label: "View details",
        icon: <VisibilityRounded fontSize="small" />,
        onSelect: () => {}
      },
      {
        id: "edit",
        label: "Edit order",
        description: canCancel ? undefined : "Only open orders can be edited",
        icon: <EditRounded fontSize="small" />,
        onSelect: () => {},
        disabled: !canCancel
      },
      {
        id: "duplicate",
        label: "Duplicate",
        icon: <ContentCopyRounded fontSize="small" />,
        onSelect: () => {}
      },
      {
        id: "cancel_order",
        label: "Cancel order",
        icon: <CancelRounded fontSize="small" />,
        onSelect: () => {},
        destructive: true,
        disabled: !canCancel
      }
    ];
  };

  return (
    <>
      <Stack spacing={2} sx={{ pt: 1 }}>
        {groups.map((g) => (
          <Box key={g.key}>
            <Typography
              variant="overline"
              sx={{
                display: "block",
                color: "text.secondary",
                letterSpacing: 0.5,
                mb: 0.5,
                px: 0.5
              }}
            >
              {formatGroupHeader(g.submittedAt, g.items.length)}
            </Typography>
            <Stack
              sx={{
                borderTop: (t) => `1px solid ${t.palette.divider}`
              }}
            >
              {g.items.map((o) => (
                <OrderRow
                  key={o.id}
                  order={o}
                  onOpenDetail={setDetailOrder}
                  onSwipeOpen={setMenuOrder}
                />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>

      <ItemActionSheet
        open={menuOrder !== null}
        onClose={() => setMenuOrder(null)}
        title={menuOrder ? `${menuOrder.asset} · ${menuOrder.side.toUpperCase()}` : ""}
        subtitle={
          menuOrder
            ? `${formatQty(menuOrder.filledQty)} @ ${formatUSD(menuOrder.avgFillPrice)} · ${formatUSD(menuOrder.totalAmount)}`
            : undefined
        }
        actions={menuOrder ? buildOrderActions(menuOrder) : []}
      />

      <OrderDetailDrawer
        order={detailOrder}
        bots={bots}
        accounts={accounts}
        showAccount={showAccount}
        onClose={() => setDetailOrder(null)}
        onApplyFilter={onApplyFilter}
        allAccountOrders={allAccountOrders ?? orders}
        tickerNames={tickerNames}
      />
    </>
  );
}

// ---- Group header formatting ----------------------------------------------

/**
 * Per-batch header. `count === 1` means a lone order — drop the clock
 * time so we don't pretend "3:41 PM" is a meaningful batch moment for
 * a single manual trade.
 */
function formatGroupHeader(iso: string, count: number): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
  if (count <= 1) return date;
  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit"
  });
  return `${date} at ${time}`;
}

// ---- Row + expand panel ---------------------------------------------------

function OrderRow({
  order,
  onOpenDetail,
  onSwipeOpen
}: {
  order: Order;
  /** Called when the user taps the row to view full details in the drawer. */
  onOpenDetail: (order: Order) => void;
  /** Called when the user swipes far enough to trigger the action sheet. */
  onSwipeOpen?: (order: Order) => void;
}) {
  const isBuy = order.side === "buy";

  // Swipe-to-reveal action sheet. Either direction triggers.
  const { handlers, translateX, consumeClickSuppression } = useSwipeReveal({
    onSwipe: () => onSwipeOpen?.(order)
  });

  return (
    <Box
      sx={{
        borderBottom: (t) => `1px solid ${t.palette.divider}`,
        "&:last-of-type": { borderBottom: 0 }
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        {...handlers}
        onClick={() => {
          // A completed swipe just fired its own action — suppress the
          // tap that ended it so the row doesn't also open the drawer.
          if (consumeClickSuppression()) return;
          onOpenDetail(order);
        }}
        sx={{
          py: 1.25,
          px: 0.5,
          cursor: "pointer",
          userSelect: "none",
          touchAction: "pan-y",
          transform: `translateX(${translateX}px)`,
          transition: translateX === 0 ? "transform 180ms ease" : "none",
          "&:hover": {
            bgcolor: (t) => alpha(t.palette.action.hover, 0.5)
          }
        }}
      >
        <TickerAvatar symbol={order.asset} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Single line — ticker + side. The asset full name lives in
           *  the detail drawer (Sergey 2026-04-25); rendering it here
           *  doubled up with the ticker when broker data set
           *  assetName === asset, and added visual noise for everyone
           *  else. */}
          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography
              variant="body1"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {order.asset}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: isBuy ? "success.main" : "error.main",
                fontWeight: 600,
                textTransform: "uppercase"
              }}
            >
              {order.side}
            </Typography>
          </Stack>
        </Box>
        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: "text.primary",
              fontFeatureSettings: '"tnum" 1'
            }}
          >
            {formatUSD(order.totalAmount)}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontFeatureSettings: '"tnum" 1'
            }}
          >
            {formatQty(order.filledQty)} @ {formatUSD(order.avgFillPrice)}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

// ---- Expand panel ---------------------------------------------------------

function OrderDetailPanel({
  order,
  bots,
  accounts,
  showAccount,
  onApplyFilter,
  onApplied,
  allAccountOrders,
  tickerNames
}: {
  order: Order;
  bots: Bot[];
  accounts?: BrokerAccount[];
  showAccount?: boolean;
  onApplyFilter?: (patch: Partial<OrdersFilterState>) => void;
  /** Optional notifier for when a filter chip is tapped — drawer-host
   *  uses this to dismiss itself so the user lands on the now-narrowed
   *  list rather than staying inside a stale detail context. */
  onApplied?: () => void;
  /** Unfiltered orders across all accounts in scope. Source for FIFO
   *  realized-P/L computation. */
  allAccountOrders: Order[];
  /** Live ticker → company-name overrides from /api/market/quotes. */
  tickerNames?: Record<string, string>;
}) {
  // Realized gain/loss via FIFO matching (Sergey 2026-04-25). Computed
  // lazily here — drawer is only mounted when the user opened it, so
  // the cost is paid for one order at a time. Splits for the symbol
  // come from the same /api/market/quotes cache the chart already
  // populated; useRealHistoricalPrices dedupes by sorted-ticker key
  // and reads through IndexedDB, so this hook does NOT trigger a new
  // server fetch when the chart already pulled the same ticker.
  const tickerKey = order.asset.toUpperCase();
  const tickersForSplits = useMemo(() => [tickerKey], [tickerKey]);
  const { splits: splitsBySymbol } = useRealHistoricalPrices(tickersForSplits);
  const fifoResult = useMemo(
    () =>
      calcOrderFifoResult(
        order,
        allAccountOrders,
        splitsBySymbol[tickerKey] ?? []
      ),
    [order, allAccountOrders, splitsBySymbol, tickerKey]
  );

  // Resolve which realized values (if any) to render. Prefer the
  // explicit fields on the order; fall back to FIFO compute.
  const realizedAmt: number | undefined =
    order.realizedProfit ??
    (order.realizedProfit === undefined
      ? fifoResult?.realized?.realizedUsd
      : undefined);
  const realizedPctVal: number | undefined =
    order.realizedProfitPct ??
    (order.realizedProfit === undefined && fifoResult?.realized
      ? fifoResult.realized.realizedFraction * 100
      : undefined);
  // Post-trade share count (signed). Always available for fillable
  // orders; null when the order didn't fill (canceled / expired / etc).
  const positionAfter: number | null = fifoResult?.positionAfter ?? null;
  // Round-trip fill counts within the open position cycle. Reset each
  // time the position returned to flat. Industry term per Sergey
  // 2026-04-25: "round trip" = one full buy-sell cycle of the same
  // instrument; the counters show how many buys / sells composed the
  // round trip up to and including this order.
  const roundTrip = fifoResult?.roundTrip ?? null;
  // Bot lookup with graceful UUID fallback. When the bot id isn't in
  // the scoped bots list (real-session orders may reference a bot
  // managed under a different account, or the /bots fetch is still in
  // flight), we shorten the UUID rather than dumping the full 36-char
  // id at the user — keeps the row readable and lets the user copy the
  // suffix if they need to look it up. The full id is preserved in the
  // tooltip via aria-label downstream.
  const lookedUpBot = order.botId
    ? bots.find((b) => b.id === order.botId)
    : null;
  const botName = order.botId
    ? lookedUpBot?.name ?? `Bot · ${order.botId.slice(-8)}`
    : "Manual order";

  // Resolve account name for aggregate "All Live Accounts" view — falls
  // back to the raw id if lookup fails (shouldn't in practice; guards
  // against stale fixture data during dev).
  const accountName = showAccount
    ? accounts?.find((a) => a.id === order.accountId)?.name ?? order.accountId
    : null;

  // Click-to-filter handlers. Each replaces (rather than toggles) so a
  // tap on "Bot: bot-live-swing" narrows the list to just that bot
  // cleanly. The filter bar's chip row gives the user a visible ×
  // affordance to clear, which is the right flow for "oh, not that".
  // Each also fires `onApplied()` so the host (drawer) can close —
  // user lands on the freshly-filtered list, not on a stale detail
  // panel for the order they just used to drill in.
  const apply = (patch: Partial<OrdersFilterState>) => {
    onApplyFilter?.(patch);
    onApplied?.();
  };
  const filterByTicker = () => apply({ search: order.asset });
  const filterByBot = () =>
    apply({ botIds: [order.botId ?? MANUAL_ORDER_BOT_ID] });
  const filterByAccount = () => apply({ accountIds: [order.accountId] });
  const filterByStatus = () => apply({ statuses: [order.status] });
  const filterByType = () => apply({ types: [order.orderType] });

  return (
    <Box
      sx={{
        px: 0.5,
        pb: 1.5,
        pt: 0.25
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          rowGap: 0.75,
          columnGap: 2,
          alignItems: "center"
        }}
      >
        {/* Ticker row — sits at the top so the expand panel opens with
         *  the asset's identity front-and-centre. Clicking writes the
         *  ticker into the search box, narrowing the list to orders on
         *  that symbol. */}
        <DetailLabel>Ticker</DetailLabel>
        <DetailValueButton
          onClick={onApplyFilter ? filterByTicker : undefined}
          ariaLabel={`Filter by ticker ${order.asset}`}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="baseline"
            justifyContent="flex-end"
            sx={{ flexWrap: "wrap", rowGap: 0.25 }}
          >
            <Box component="span" sx={{ fontWeight: 700 }}>
              {order.asset}
            </Box>
            {/* Asset full name. Live name from /api/market/quotes
             *  takes priority; falls back to the adapter's stamped
             *  assetName (which itself has the static-table fallback)
             *  or the ticker. Suppress the row when the resolved name
             *  is identical to the ticker (broker returned the symbol
             *  as the "name" — duplicate doesn't help). */}
            {(() => {
              const liveName = tickerNames?.[order.asset.toUpperCase()];
              const display =
                liveName && liveName.toUpperCase() !== order.asset.toUpperCase()
                  ? liveName
                  : order.assetName && order.assetName !== order.asset
                    ? order.assetName
                    : null;
              if (!display) return null;
              return (
                <Box
                  component="span"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 400,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    textAlign: "right"
                  }}
                >
                  {display}
                </Box>
              );
            })()}
          </Stack>
        </DetailValueButton>

        <DetailLabel>Status</DetailLabel>
        <Box sx={{ textAlign: "right" }}>
          <StatusPill
            status={order.status}
            onClick={onApplyFilter ? filterByStatus : undefined}
          />
        </Box>

        <DetailLabel>Order type</DetailLabel>
        <DetailValueButton
          onClick={onApplyFilter ? filterByType : undefined}
          ariaLabel={`Filter by order type ${order.orderType}`}
        >
          {formatOrderType(order)}
        </DetailValueButton>

        <DetailLabel>Quantity</DetailLabel>
        <DetailValue>{formatQty(order.filledQty)}</DetailValue>

        <DetailLabel>Avg cost</DetailLabel>
        <DetailValue>{formatUSD(order.avgFillPrice)}</DetailValue>

        <DetailLabel>Total</DetailLabel>
        <DetailValue>{formatUSD(order.totalAmount)}</DetailValue>

        {/* Realized profit row used to live here. Moved below "Filled"
         *  per Sergey 2026-04-25 so the lifecycle of the order
         *  (Submitted → Filled → realized) reads chronologically. */}

        {order.notional !== undefined && (
          <>
            <DetailLabel>Notional</DetailLabel>
            <DetailValue>{formatUSD(order.notional)}</DetailValue>
          </>
        )}

        <DetailLabel>Bot</DetailLabel>
        <DetailValueButton
          onClick={onApplyFilter ? filterByBot : undefined}
          ariaLabel={`Filter by bot ${botName}`}
        >
          {botName}
        </DetailValueButton>

        {accountName && (
          <>
            <DetailLabel>Account</DetailLabel>
            <DetailValueButton
              onClick={onApplyFilter ? filterByAccount : undefined}
              ariaLabel={`Filter by account ${accountName}`}
            >
              {accountName}
            </DetailValueButton>
          </>
        )}

        {/* `stateDetail` used to render here as a "State" row, but the
         *  mock values ("Completed", "CompletedPartially", "Canceled",
         *  "Expired") are just a vocabulary-swap of `status` — the
         *  Status pill above conveys the same fact, one tap, one
         *  filter. Dropping the row removes the duplicate. We keep the
         *  field on `Order` for the day a real broker response carries
         *  richer detail (e.g. "Rejected: insufficient buying power"),
         *  at which point we can re-introduce the row with content
         *  that isn't redundant. */}

        <DetailLabel>Submitted</DetailLabel>
        <DetailValue>{formatDetailTimestamp(order.submittedAt)}</DetailValue>

        {order.filledAt && (
          <>
            <DetailLabel>Filled</DetailLabel>
            <DetailValue>{formatDetailTimestamp(order.filledAt)}</DetailValue>
          </>
        )}

        {/* Realized gain/loss — FIFO match against earlier fills on the
         *  same (account, ticker), with split adjustments. Only renders
         *  when the order actually closed something. Pure opening fills
         *  produce `null` from the helper and the row is suppressed. */}
        {realizedAmt !== undefined && (
          <>
            <DetailLabel>Realized gain&#x202F;(loss)</DetailLabel>
            <RealizedProfitValue amount={realizedAmt} pct={realizedPctVal} />
          </>
        )}

        {/* Position — running share count after this fill. Industry-
         *  standard label (IBKR / Schwab / Webull all use
         *  "Position"). Always rendered for fillable orders so the user
         *  can see the resulting size whether the order opened or
         *  closed. "0 shares" when the trade flattens the holding. */}
        {positionAfter !== null && (
          <>
            <DetailLabel>Position</DetailLabel>
            <DetailValue>{formatPositionAfter(positionAfter)}</DetailValue>
          </>
        )}

        {/* Round trip — buy + sell fill counts within the current open
         *  position cycle. "Round trip" is the recognized industry term
         *  for one full buy-sell cycle of the same instrument; the
         *  counts reset every time the position returns to flat, so
         *  this row tracks the trip in progress (and, on the closing
         *  fill, the final tally of the trip just completed). */}
        {roundTrip !== null && (
          <>
            <DetailLabel>Round trip</DetailLabel>
            <DetailValue>{formatRoundTrip(roundTrip)}</DetailValue>
          </>
        )}

        {order.canceledAt && (
          <>
            <DetailLabel>Canceled</DetailLabel>
            <DetailValue>
              {formatDetailTimestamp(order.canceledAt)}
            </DetailValue>
          </>
        )}
      </Box>
    </Box>
  );
}

function DetailLabel({ children }: { children: React.ReactNode }) {
  // body1 (1rem) so detail labels match the "Buying Power" scale that
  // Sergey froze as the canonical clickable-text size for the page.
  return (
    <Typography
      variant="body1"
      sx={{
        color: "text.secondary",
        pr: 2,
        alignSelf: "center"
      }}
    >
      {children}
    </Typography>
  );
}

function DetailValue({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="body1"
      sx={{
        color: "text.primary",
        textAlign: "right",
        fontFeatureSettings: '"tnum" 1',
        wordBreak: "break-word"
      }}
    >
      {children}
    </Typography>
  );
}

/**
 * Clickable variant of `DetailValue`. When `onClick` is provided it
 * renders as a pressable `ButtonBase` with a dotted underline and a
 * primary-tinted colour to signal "tap to filter". When `onClick` is
 * `undefined`, it degrades to the plain text treatment so the panel
 * still reads cleanly for callers that haven't wired click-to-filter.
 *
 * We use `ButtonBase` (not a real button) because the contents are
 * inline-ish text — a real `Button` would add its own typography and
 * padding that fight the grid. `ButtonBase` gives us keyboard focus,
 * ripple, and ARIA semantics without the layout baggage.
 */
function DetailValueButton({
  onClick,
  ariaLabel,
  children
}: {
  onClick?: () => void;
  ariaLabel?: string;
  children: React.ReactNode;
}) {
  if (!onClick) {
    return <DetailValue>{children}</DetailValue>;
  }
  return (
    <Box sx={{ textAlign: "right" }}>
      <ButtonBase
        onClick={(e) => {
          // Prevent the row's outer onClick from firing when a user
          // taps on an inner value.
          e.stopPropagation();
          onClick();
        }}
        aria-label={ariaLabel}
        sx={{
          typography: "body1",
          color: "primary.main",
          textAlign: "right",
          fontFeatureSettings: '"tnum" 1',
          borderRadius: 0.5,
          px: 0.5,
          py: 0.25,
          mr: -0.5,
          // Allow long values (e.g. full bot names) to wrap onto a
          // second line rather than overflow the right column. The
          // grid row that hosts this button auto-grows to fit, so
          // multi-line values don't break the table-like layout.
          display: "inline-block",
          maxWidth: "100%",
          whiteSpace: "normal",
          wordBreak: "break-word",
          textDecoration: "underline dotted",
          textUnderlineOffset: 3,
          textDecorationColor: (t: Theme) => alpha(t.palette.primary.main, 0.4),
          transition: "background-color 120ms ease",
          "&:hover": {
            bgcolor: (t: Theme) => alpha(t.palette.primary.main, 0.08),
            textDecorationColor: (t: Theme) => t.palette.primary.main
          },
          "&:focus-visible": {
            outline: (t: Theme) => `2px solid ${t.palette.primary.main}`,
            outlineOffset: 2
          }
        }}
      >
        {children}
      </ButtonBase>
    </Box>
  );
}

/**
 * Dollar + percentage value colored by sign — green for gains, red for
 * losses, muted for exactly zero. Matches the price-change treatment
 * elsewhere in the app (e.g. the portfolio hero's Today chip) so users
 * parse the sign at a glance without reading the number.
 *
 * Pct is optional: we show it in parens when present, otherwise we
 * render the dollar figure alone. The order mock always populates both
 * but real broker data may carry just one.
 */
// ---- Detail drawer --------------------------------------------------------

/**
 * Bottom-sheet drawer that hosts `OrderDetailPanel`. Mirrors the
 * BalancesDrawer pattern used for "Buying power" so order details get
 * the same dedicated context — header echo (logo + ticker + side +
 * total) on top, full detail grid below, dismiss-on-filter so a
 * click-to-filter chip lands the user on the now-narrowed list rather
 * than stranded in stale detail.
 */
function OrderDetailDrawer({
  order,
  bots,
  accounts,
  showAccount,
  onClose,
  onApplyFilter,
  allAccountOrders,
  tickerNames
}: {
  order: Order | null;
  bots: Bot[];
  accounts?: BrokerAccount[];
  showAccount?: boolean;
  onClose: () => void;
  onApplyFilter?: (patch: Partial<OrdersFilterState>) => void;
  /** Unfiltered orders used as input to the FIFO realized-P/L helper. */
  allAccountOrders: Order[];
  tickerNames?: Record<string, string>;
}) {
  // Hold the last non-null order so the drawer keeps rendering content
  // through its slide-out animation. Without this, the body would clear
  // the moment we set `detailOrder = null` and the user would see a
  // flash of empty drawer for ~200ms.
  const [snapshot, setSnapshot] = useState<Order | null>(order);
  if (order && order !== snapshot) {
    // Direct setState during render is fine because we're guarding on a
    // changed identity — React de-dupes the resulting commit.
    setSnapshot(order);
  }

  const visible = snapshot;
  if (!visible) return null;
  const isBuy = visible.side === "buy";

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={order !== null}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      disableDiscovery
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          pb: "env(safe-area-inset-bottom)",
          maxHeight: "92vh",
          ...desktopBottomDrawerPaperSx,
        }
      }}
    >
      {/* Grab handle */}
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 0.5 }}>
        <Box
          sx={(t: Theme) => ({
            width: 36,
            height: 4,
            borderRadius: 2,
            bgcolor: t.palette.divider
          })}
        />
      </Box>

      {/* Header echo — logo + ticker + side + total. Same visual
       *  identity as the row the user just tapped. */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, pt: 1, pb: 1.5 }}>
        <TickerAvatar symbol={visible.asset} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {visible.asset}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: isBuy ? "success.main" : "error.main",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.5
              }}
            >
              {visible.side}
            </Typography>
          </Stack>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontFeatureSettings: '"tnum" 1',
              lineHeight: 1.1
            }}
          >
            {formatUSD(visible.totalAmount)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontFeatureSettings: '"tnum" 1'
            }}
          >
            {formatQty(visible.filledQty)} @ {formatUSD(visible.avgFillPrice)}
          </Typography>
        </Box>
      </Stack>

      <Divider />

      <Box sx={{ px: 2, py: 1.5, overflowY: "auto" }}>
        <OrderDetailPanel
          order={visible}
          bots={bots}
          accounts={accounts}
          showAccount={showAccount}
          onApplyFilter={onApplyFilter}
          onApplied={onClose}
          allAccountOrders={allAccountOrders}
          tickerNames={tickerNames}
        />
      </Box>

      <Divider />

      <Box sx={{ p: 1.5 }}>
        <Button
          fullWidth
          variant="text"
          color="inherit"
          onClick={onClose}
          sx={{ fontWeight: 500, py: 1 }}
        >
          Close
        </Button>
      </Box>
    </SwipeableDrawer>
  );
}

function RealizedProfitValue({
  amount,
  pct
}: {
  amount: number;
  pct?: number;
}) {
  // `≥ 0` rather than `> 0` treats a perfect-wash trade as green (user
  // at least got out whole). Exactly-zero realized is rare in practice
  // but it shouldn't render as red.
  const positive = amount >= 0;
  const color = (t: Theme) =>
    amount === 0
      ? t.palette.text.secondary
      : positive
        ? t.palette.success.main
        : t.palette.error.main;

  const sign = positive ? "+" : "−"; // minus sign char (U+2212) reads nicer than `-`
  const abs = Math.abs(amount);

  return (
    <Typography
      variant="body1"
      sx={{
        color,
        fontWeight: 600,
        textAlign: "right",
        fontFeatureSettings: '"tnum" 1'
      }}
    >
      {sign}
      {formatUSD(abs)}
      {pct !== undefined && (
        <Box component="span" sx={{ ml: 0.75, fontWeight: 500, opacity: 0.85 }}>
          ({sign}
          {Math.abs(pct).toFixed(2)}%)
        </Box>
      )}
    </Typography>
  );
}

// ---- Small presentation helpers -------------------------------------------

function StatusPill({
  status,
  onClick
}: {
  status: OrderStatus;
  /**
   * Optional filter-by-status handler. When provided, the chip becomes
   * clickable (with MUI's `clickable` affordance) and stops propagation
   * so tapping the pill doesn't collapse the expand panel. When
   * omitted, the pill reads as a static status label.
   */
  onClick?: () => void;
}) {
  const palette = STATUS_PALETTE[status];
  return (
    <Chip
      label={palette.label}
      size="small"
      clickable={Boolean(onClick)}
      onClick={
        onClick
          ? (e) => {
              e.stopPropagation();
              onClick();
            }
          : undefined
      }
      aria-label={onClick ? `Filter by status ${palette.label}` : undefined}
      sx={(theme: Theme) => ({
        bgcolor: alpha(palette.color(theme), 0.14),
        color: palette.color(theme),
        fontWeight: 600,
        borderRadius: 999,
        height: 22,
        "& .MuiChip-label": { px: 1.25, fontSize: 11, letterSpacing: 0.3 },
        ...(onClick
          ? {
              "&:hover": {
                bgcolor: alpha(palette.color(theme), 0.24)
              }
            }
          : {})
      })}
    />
  );
}

const STATUS_PALETTE: Record<
  OrderStatus,
  { label: string; color: (t: Theme) => string }
> = {
  filled: { label: "Filled", color: (t) => t.palette.success.main },
  partial: { label: "Partial", color: (t) => t.palette.warning.main },
  open: { label: "Open", color: (t) => t.palette.info.main },
  canceled: { label: "Canceled", color: (t) => t.palette.text.secondary },
  expired: { label: "Expired", color: (t) => t.palette.text.secondary },
  replaced: { label: "Replaced", color: (t) => t.palette.info.main }
};

function formatOrderType(o: Order): string {
  switch (o.orderType) {
    case "market":
      return "Market";
    case "limit":
      return o.limitPrice !== undefined
        ? `Limit @ ${formatUSD(o.limitPrice)}`
        : "Limit";
    case "stop":
      return o.stopPrice !== undefined
        ? `Stop @ ${formatUSD(o.stopPrice)}`
        : "Stop";
    case "stop_limit":
      return o.stopPrice !== undefined && o.limitPrice !== undefined
        ? `Stop-limit ${formatUSD(o.stopPrice)} / ${formatUSD(o.limitPrice)}`
        : "Stop-limit";
    case "pending":
      // Release window comes from `scheduledFor` when present (the time
      // WHB is supposed to push this to the broker). When omitted, a
      // plain "Pending" label still reads sensibly.
      return o.scheduledFor
        ? `Pending · releases ${formatScheduleHint(o.scheduledFor)}`
        : "Pending";
    case "scheduled":
      // Recurring rule rendered as a short cadence hint.
      return o.scheduleRule
        ? `Scheduled · ${o.scheduleRule}`
        : "Scheduled";
    case "smart":
      // Smart orders carry a strategy label ("VWAP 30min",
      // "Bracket +2% / −1%"). Falls back to plain "Smart".
      return o.smartStrategy ? `Smart · ${o.smartStrategy}` : "Smart";
    case "correction":
      // Synthetic reconciliation order — not a real broker fill.
      // Displayed so the user can see the cash delta being applied
      // to close the gap between reconstruction and Alpaca's books.
      return "Correction · reconciliation";
  }
}

/**
 * Short cadence/time hint used inside "Pending · releases ..." so the
 * row stays narrow. Falls back to the browser's locale short format.
 */
function formatScheduleHint(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

/**
 * Quantity formatter — whole shares render without decimals, fractional
 * shares keep up to 6 digits. Trailing zeros trimmed so we don't render
 * "3.000000".
 */
function formatQty(n: number): string {
  if (Number.isInteger(n)) return n.toString();
  return n.toFixed(6).replace(/\.?0+$/, "");
}

/**
 * Format the post-trade running position. Sign carries direction:
 *   positive →  "{n} shares"
 *   negative → "-{n} shares" (short)
 *   zero     →  "0 shares" (flat — Sergey 2026-04-25 explicit)
 *
 * Pluralization left out per industry convention — Schwab / IBKR
 * write "1 shares" too; "share" / "shares" toggle is needless noise.
 */
function formatPositionAfter(signedQty: number): string {
  if (Math.abs(signedQty) < 1e-9) return "0 shares";
  // Round small float residue and reuse the share-count formatter so
  // fractional positions like 43.309112 render the same way they do
  // in the Quantity row above.
  const abs = Math.abs(signedQty);
  const sign = signedQty < 0 ? "-" : "";
  return `${sign}${formatQty(abs)} shares`;
}

/**
 * Format the round-trip fill counts as "{N} buys · {M} sells". The
 * middle dot mirrors the separator used elsewhere in the panel (e.g.
 * "qty @ price"). Both counts always render even when one is zero
 * so the user can see the imbalance at a glance.
 */
function formatRoundTrip(rt: { buys: number; sells: number }): string {
  return `${rt.buys} buys · ${rt.sells} sells`;
}

function formatDetailTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}
